import * as React from 'react';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { config } from '@/config';
import { paths } from '@/paths';
import { AuthStrategy } from '@/lib/auth/strategy';
import { useUser } from '@/hooks/use-user';

const SESSION_CHECK_INTERVAL = 2000;

export function AuthGuard({ children }) {
  const navigate = useNavigate();
  const { user, error, isLoading } = useUser();
  const [isChecking, setIsChecking] = React.useState(true);

  const redirectToSignIn = React.useCallback(() => {
    // logger.debug('[AuthGuard]: Redirecting to sign in');
    switch (config.auth.strategy) {
      case AuthStrategy.CUSTOM:
        window.location.assign(paths.auth.custom.signIn);
        break;
        case AuthStrategy.AUTH0:
          navigate(paths.auth.auth0.signIn, { replace: true });
          break;
        case AuthStrategy.COGNITO:
          navigate(paths.auth.cognito.signIn, { replace: true });
          break;
        case AuthStrategy.FIREBASE:
          navigate(paths.auth.firebase.signIn, { replace: true });
          break;
        case AuthStrategy.SUPABASE:
          navigate(paths.auth.supabase.signIn, { replace: true });
          break;
      default:
        console.error('[AuthGuard]: Unknown auth strategy');
    }
  }, [navigate]);

  const checkServerSession = React.useCallback(async () => {
    try {
      const response = await fetch('/api/mysessionCheck', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Session check failed');
      }

      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('[AuthGuard]: Error checking session:', error);
      return false;
    }
  }, []);

  const checkPermissions = React.useCallback(async () => {
    if (isLoading) {
      return;
    }

    if (error) {
      setIsChecking(false);
      return;
    }

    if (!user) {
      console.warn('[AuthGuard]: User is not logged in, redirecting to sign in');
      redirectToSignIn();
    } else {
      const isSessionValid = await checkServerSession();
      if (!isSessionValid) {
        console.warn('[AuthGuard]: Server session expired, redirecting to sign in');
        localStorage.setItem('sessionTimedout', 'true');
        redirectToSignIn();
      } else {
        setIsChecking(false);
      }
    }
  }, [isLoading, error, user, redirectToSignIn, checkServerSession]);

  React.useEffect(() => {
    checkPermissions().catch((err) => {
      console.error('[AuthGuard]: Error in checkPermissions:', err);
      setIsChecking(false);
    });

    const intervalId = setInterval(async () => {
      const isSessionValid = await checkServerSession();
      if (!isSessionValid) {
        console.warn('[AuthGuard]: Server session expired, redirecting to sign in');
        localStorage.setItem('sessionTimedout', 'true');
        clearInterval(intervalId);
        redirectToSignIn();
      }
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [checkPermissions, checkServerSession, redirectToSignIn]);

  if (isChecking) {
    // You can return a loading indicator here if you prefer
    return null;
  }

  if (error) {
    return <Alert color="error">{error}</Alert>;
  }

  return <React.Fragment>{children}</React.Fragment>;
}
